import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const fmt = (v, d = 2) => Number(v || 0).toFixed(d);

export default function FloorPlans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/floorplan/layouts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const hay = [r.plan_number, r.name, r.client?.company, r.client?.name, r.created_by_name]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const del = async (id) => {
    if (!window.confirm("Delete this floor plan?")) return;
    try {
      await api.delete(`/floorplan/layouts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Floor Plan · Pipeline"
        title="Store / Warehouse Layouts"
        actions={
          <button onClick={() => navigate("/floorplan/new")} className="btn-primary" data-testid="fp-new-btn">
            <Plus size={16} /> New layout
          </button>
        }
      />
      <div className="p-8">
        <div className="panel">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-line">
            <Search size={16} className="text-ink-faint" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 outline-none bg-transparent text-sm"
              placeholder="Search…"
              data-testid="fp-search"
            />
          </div>
          {filtered.length === 0 ? (
            <div className="p-16 text-center text-ink-muted">
              <div className="label-tech">No layouts yet — click "New layout" to draw one.</div>
            </div>
          ) : (
            <table className="w-full grid-lines">
              <thead>
                <tr>
                  <th>Plan #</th>
                  <th>Name</th>
                  <th>Client</th>
                  <th>Unit</th>
                  <th className="num">Corners</th>
                  <th className="num">Perimeter</th>
                  <th className="num">Area</th>
                  <th>Created by</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="cursor-pointer hover:bg-surfaceAlt"
                    onClick={() => navigate(`/floorplan/${r.id}`)}
                    data-testid={`fp-row-${r.plan_number}`}
                  >
                    <td className="mono font-bold">{r.plan_number}</td>
                    <td>{r.name}</td>
                    <td>{r.client?.company || r.client?.name || "—"}</td>
                    <td className="mono text-xs">{r.unit}</td>
                    <td className="num">{r.points?.length || 0}</td>
                    <td className="num mono text-xs">{fmt(r.perimeter)} {r.unit}</td>
                    <td className="num mono text-xs font-bold">{fmt(r.area)} {r.unit}²</td>
                    <td className="text-xs">{r.created_by_name}</td>
                    <td>
                      {user?.role === "admin" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); del(r.id); }}
                          className="btn-danger text-xs py-1"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
